import { AnyExtension, Extensions } from '@tiptap/core';
import HardBreak from '@tiptap/extension-hard-break';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';

import { ClearMarksOnEnter } from './custom-extensions/ClearMarksOnEnter';

/**
 * Override the default hard break behavior to allow for
 * the shift + enter key combination to create a new line.
 */
export const CustomHardBreak = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.enter(),
    };
  },
});

function escapeMarkdownLinkComponent(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/[()"]/g, '\\$&');
}

/**
 * @tiptap/extension-underline has no built-in markdown spec, so
 * tiptap-markdown silently strips underline marks during serialization.
 * We add ++/++ markers here so underlined text round-trips correctly;
 * the rendering side already parses ++text++ via remark-ins in useMessageComponents.
 */
const MarkdownUnderline = Underline.extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '++',
          close: '++',
          expelEnclosingWhitespace: true,
        },
        parse: {
          // handled by markdown-it
        },
      },
    };
  },
});

/**
 * Always use [text](url) format instead of the default <url>
 * autolink format which renders raw angle brackets in chat.
 */
const MarkdownLink = Link.extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '[',
          close(
            _state: unknown,
            mark: { attrs: { href: string; title?: string } },
          ) {
            const href = escapeMarkdownLinkComponent(mark.attrs.href);
            const title = mark.attrs.title
              ? ` "${escapeMarkdownLinkComponent(mark.attrs.title)}"`
              : '';
            return `](${href}${title})`;
          },
          mixable: true,
        },
        parse: {
          // handled by markdown-it
        },
      },
    };
  },
}).configure({
  openOnClick: false,
});

interface MessageEditorExtensionsOptions {
  placeholder?: string;
  additionalExtensions?: AnyExtension[];
}

const StarterKitExtension = StarterKit.configure({
  heading: {
    levels: [2, 3],
  },
  bulletList: {
    HTMLAttributes: {
      class: 'list-disc list-outside pl-5 mt-0.5 mb-2 ml-2.5',
    },
  },
}) as AnyExtension;

/**
 * Returns the base set of tiptap extensions used for message-style editors
 * (chat input, welcome message settings, etc.).
 *
 * Pass chat-specific extensions (CharacterCount, SuggestionCommand, etc.)
 * via `additionalExtensions`.
 */
export function getMessageEditorExtensions(
  options?: MessageEditorExtensionsOptions,
): Extensions {
  return [
    StarterKitExtension,
    Markdown,
    ClearMarksOnEnter,
    Placeholder.configure({
      emptyEditorClass:
        'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
      placeholder: options?.placeholder ?? '',
    }),
    MarkdownUnderline,
    MarkdownLink,
    CustomHardBreak,
    ListKeymap,
    ...(options?.additionalExtensions ?? []),
  ];
}
